import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, CheckCircle2, XCircle, Wallet, FileText, Receipt, Car } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  submitted:  "bg-amber-50 text-amber-800 border-amber-200",
  approved:   "bg-blue-50 text-blue-700 border-blue-200",
  rejected:   "bg-red-50 text-red-700 border-red-200",
  reimbursed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function fmtEuro(n) {
  return (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}


export default function ExpenseReportView() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const canValidate = hasPermission("expenses.validate");

  const load = async () => {
    try {
      // The list endpoint returns everything the user is allowed to see —
      // fetch and find. Small tenant volumes, acceptable for now.
      const r = await api.get("/expense-reports");
      const found = (r.data.reports || []).find((x) => x.id === id);
      if (!found) {
        // Fallback: maybe the user owns it but doesn't have expenses.validate
        // (mine=true endpoint).
        const r2 = await api.get("/expense-reports?mine=true");
        const own = (r2.data.reports || []).find((x) => x.id === id);
        if (!own) throw new Error("Note introuvable");
        setRep(own);
        return;
      }
      setRep(found);
    } catch (e) { toast.error(apiError(e)); navigate("/app/expenses"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!rep) return <div className="p-8 text-sm text-slate-500">Chargement…</div>;

  const canAct = canValidate && rep.status === "submitted";
  const canReimburse = canValidate && rep.status === "approved";

  const doApprove = async () => {
    try {
      await api.post(`/expense-reports/${rep.id}/approve`);
      toast.success("Note approuvée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doReimburse = async () => {
    try {
      await api.post(`/expense-reports/${rep.id}/reimburse`);
      toast.success("Note marquée comme remboursée.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="expense-report-view">
      <button className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              onClick={() => navigate("/app/expenses")}
              data-testid="detail-back-btn">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        {t("expenseReports.detail.back")}
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            {t("expenseReports.title")}
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {rep.period_label}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge variant="outline"
                   className={cn("text-[10px] font-medium", STATUS_STYLE[rep.status])}
                   data-testid="detail-status-badge">
              {t(`expenseReports.status.${rep.status}`)}
            </Badge>
            <span className="text-xs text-slate-500">
              {t("expenseReports.detail.author")} : <strong className="text-slate-700">{rep.created_by_email}</strong>
            </span>
            <span className="text-xs text-slate-500">
              {t("expenseReports.detail.submittedAt")} {fmtDate(rep.created_at)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {canAct && (
            <>
              <Button onClick={doApprove} data-testid="detail-approve-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={2} />
                {t("expenseReports.detail.approve")}
              </Button>
              <Button variant="outline" onClick={() => setShowReject(true)}
                      data-testid="detail-reject-btn">
                <XCircle className="h-4 w-4 mr-1.5" strokeWidth={2} />
                {t("expenseReports.detail.reject")}
              </Button>
            </>
          )}
          {canReimburse && (
            <Button onClick={doReimburse} data-testid="detail-reimburse-btn">
              <Wallet className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {t("expenseReports.detail.reimburse")}
            </Button>
          )}
        </div>
      </div>

      <Card className="p-6 border-slate-200 rounded-xl bg-white">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            Total
          </div>
          <div className="text-3xl font-semibold text-slate-900"
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="detail-total">
            {fmtEuro(rep.total_amount)}
          </div>
        </div>
      </Card>

      {rep.rejected_reason && (
        <Card className="p-4 border-red-200 bg-red-50/40 rounded-xl" data-testid="detail-rejected-reason">
          <div className="text-[10px] font-medium uppercase tracking-widest text-red-700 mb-1">
            {t("expenseReports.detail.rejectedReason")}
          </div>
          <div className="text-sm text-red-900">{rep.rejected_reason}</div>
        </Card>
      )}

      <Card className="p-6 border-slate-200 rounded-xl bg-white">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-3">
          Lignes ({rep.items?.length || 0})
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>PCG</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rep.items || []).map((it, i) => (
              <TableRow key={i} data-testid={`detail-item-${i}`}>
                <TableCell>
                  {it.kind === "mileage" ? (
                    <Car className="h-4 w-4 text-emerald-600" strokeWidth={2} />
                  ) : (
                    <Receipt className="h-4 w-4 text-blue-600" strokeWidth={2} />
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-900">
                  {it.description}
                  {it.kind === "mileage" && (
                    <div className="text-[11px] text-slate-500">
                      {it.km_distance} km — {it.fiscal_horsepower} CV{it.electric && " · élec"}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDate(it.expense_date)}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{it.pcg_code || "—"}</TableCell>
                <TableCell className="text-right text-sm font-medium text-slate-900">
                  {it.kind === "mileage"
                    ? fmtEuro(it.computed_amount)
                    : fmtEuro(it.amount_ttc)}
                </TableCell>
                <TableCell className="text-right">
                  {it.file_asset_id && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={`${API}/files/${it.file_asset_id}`} target="_blank" rel="noreferrer">
                        <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                      </a>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <RejectDialog open={showReject} onOpenChange={setShowReject} rep={rep} onDone={load} t={t} />
    </div>
  );
}


function RejectDialog({ open, onOpenChange, rep, onDone, t }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error(t("expenseReports.reject.reasonLabel")); return; }
    setSaving(true);
    try {
      await api.post(`/expense-reports/${rep.id}/reject`, { reason: reason.trim() });
      toast.success("Note refusée.");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-report-dialog">
        <DialogHeader>
          <DialogTitle>{t("expenseReports.reject.title")}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label>{t("expenseReports.reject.reasonLabel")}</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder={t("expenseReports.reject.reasonPlaceholder")}
                    data-testid="reject-report-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button variant="destructive" onClick={submit}
                  disabled={saving || !reason.trim()}
                  data-testid="reject-report-submit">
            {saving ? "…" : t("expenseReports.reject.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
